/**
 * PAPPO — MessageInput Component
 * Campo de texto para enviar mensagens com botão de envio (ícone).
 * Suporta anexos de imagem (com legenda opcional) e um seletor de emojis.
 * Cresce com o conteúdo (textarea auto-resize).
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  type KeyboardEvent,
  type FormEvent,
  type ChangeEvent,
} from 'react';
import { Send, ImagePlus, Camera, Video, Clapperboard, Smile, Sticker, X, Mic, Trash2, Plus, Reply } from 'lucide-react';
import { uploadImage, uploadAudio, uploadVideo } from '../../services/uploads';
import { compressImage } from '../../utils/image';
import {
  searchStickers,
  getTrendingStickers,
  pickSticker,
  type StickerResult,
} from '../../services/stickers';
import { getSuggestions } from './typingSuggestions';
import styles from './MessageInput.module.css';

/** Tempo de inatividade (ms) até considerar que parou de escrever */
const TYPING_IDLE_MS = 1500;

/** Duração máxima de uma nota de voz (segundos) — evita anexos > 16 MB */
const MAX_REC_SECONDS = 120;

/** Teto de tamanho de vídeo (MB) — espelha o limite do backend (MAX_UPLOAD_VIDEO_MB) */
const MAX_VIDEO_MB = 50;

/** Formata segundos como m:ss (ex.: 75 → "1:15") */
function formatSeconds(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Emojis de acesso rápido (sem dependência npm, sem mexer no CSP) */
const EMOJIS = [
  '😀', '😂', '😍', '👍', '❤️', '🎉',
  '😢', '😮', '🙏', '🔥', '😅', '😎',
  '🥳', '😱', '🤔', '👏', '🙌', '💩',
];

interface MessageInputProps {
  /** Callback ao enviar mensagem de texto (opcionalmente citando outra) */
  onSend: (content: string, replyToId?: number | null) => void;
  /** Callback ao enviar imagem (url do anexo + legenda opcional) */
  onSendImage?: (url: string, caption: string) => void;
  /** Callback ao enviar figurinha (url interna /uploads já rehospedada) */
  onSendSticker?: (url: string) => void;
  /** Callback ao enviar nota de voz (url interna /uploads) */
  onSendAudio?: (url: string) => void;
  /** Callback ao enviar vídeo (url interna /uploads + legenda opcional) */
  onSendVideo?: (url: string, caption: string) => void;
  /** Notifica início/fim de digitação (para indicador "a escrever...") */
  onTyping?: (isTyping: boolean) => void;
  /** Placeholder customizado */
  placeholder?: string;
  /** Desabilitar input */
  disabled?: boolean;
  /** Mensagem sendo citada (mostra o banner de resposta acima do campo) */
  replyingTo?: { id: number; author: string; text: string } | null;
  /** Cancela a resposta em curso */
  onCancelReply?: () => void;
}

export function MessageInput({
  onSend,
  onSendImage,
  onSendSticker,
  onSendAudio,
  onSendVideo,
  onTyping,
  placeholder = 'Escreva uma mensagem...',
  disabled = false,
  replyingTo = null,
  onCancelReply,
}: MessageInputProps) {
  const [value, setValue] = useState('');
  /* Campo focado? Só mostramos a barra de sugestões enquanto está focado. */
  const [focused, setFocused] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  /* Menu de anexos recolhido num "+" (estilo WhatsApp): foto, câmera, figurinha */
  const [showAttach, setShowAttach] = useState(false);
  /* Seletor de figurinhas (Giphy via backend) */
  const [showStickers, setShowStickers] = useState(false);
  const [stickerQuery, setStickerQuery] = useState('');
  const [stickerResults, setStickerResults] = useState<StickerResult[]>([]);
  const [stickersLoading, setStickersLoading] = useState(false);
  /* Celular/tablet = ponteiro de toque; desktop = ponteiro fino → sem câmera.
     Detecta uma única vez (o tipo de dispositivo não muda em runtime). */
  const [isMobile] = useState(() => window.matchMedia('(pointer: coarse)').matches);
  const [preview, setPreview] = useState<string | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  /* Vídeo como ANEXO: pré-visualização local + url pendente (envia com legenda,
     igual à imagem — a pessoa escreve uma mensagem junto antes de mandar) */
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [pendingVideoUrl, setPendingVideoUrl] = useState<string | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  /* Gravação de voz (MediaRecorder) */
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [audioUploading, setAudioUploading] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const videoCaptureInputRef = useRef<HTMLInputElement>(null);
  /* Estado de digitação com debounce (evita spam de eventos no socket) */
  const isTypingRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Recursos da gravação — refs para não recriar handlers a cada segundo */
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recCancelledRef = useRef(false);

  const stopTyping = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (isTypingRef.current) {
      isTypingRef.current = false;
      onTyping?.(false);
    }
  }, [onTyping]);

  const signalTyping = useCallback(() => {
    if (!onTyping) return;
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      onTyping(true);
    }
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(stopTyping, TYPING_IDLE_MS);
  }, [onTyping, stopTyping]);

  /* Limpa timer e sinaliza fim ao desmontar/trocar de conversa */
  useEffect(() => stopTyping, [stopTyping]);

  const resetHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const clearImage = useCallback(() => {
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPendingImageUrl(null);
  }, []);

  const clearVideo = useCallback(() => {
    setVideoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPendingVideoUrl(null);
  }, []);

  /* ── Gravação de voz (MediaRecorder) ──────────────────────────── */
  const _cleanupRecording = () => {
    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }
    audioStreamRef.current?.getTracks().forEach((t) => t.stop()); // liberta o microfone
    audioStreamRef.current = null;
  };

  /* Para o MediaRecorder; `cancelled` distingue descartar de enviar (lido no onstop) */
  const finishRecording = useCallback((cancelled: boolean) => {
    recCancelledRef.current = cancelled;
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop(); // dispara onstop
    setRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    if (disabled || uploading || audioUploading || recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      alert('O seu navegador não suporta gravação de áudio.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      alert('Não foi possível aceder ao microfone. Autorize o acesso e tente de novo.');
      return;
    }
    audioStreamRef.current = stream;
    audioChunksRef.current = [];
    recCancelledRef.current = false;

    const mr = new MediaRecorder(stream);
    mediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      _cleanupRecording();
      const cancelled = recCancelledRef.current;
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      audioChunksRef.current = [];
      mediaRecorderRef.current = null;
      if (cancelled || blob.size === 0) return; // cancelado ou silêncio → não envia
      setAudioUploading(true);
      const res = await uploadAudio(blob);
      setAudioUploading(false);
      if (res.success && res.data) onSendAudio?.(res.data.attachment_url);
      else alert('Falha ao enviar o áudio.');
    };

    mr.start();
    setRecording(true);
    setRecSeconds(0);
    recTimerRef.current = setInterval(() => {
      setRecSeconds((s) => {
        const next = s + 1;
        if (next >= MAX_REC_SECONDS) finishRecording(false); // auto-envia no limite
        return next;
      });
    }, 1000);
  }, [disabled, uploading, audioUploading, recording, onSendAudio, finishRecording]);

  /* Ao desmontar/trocar de conversa: para a gravação e liberta o microfone */
  useEffect(() => {
    return () => {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') {
        recCancelledRef.current = true;
        mr.stop();
      }
      _cleanupRecording();
    };
  }, []);

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    // Enter respeita o mesmo estado do botão (não enviar durante upload).
    if (disabled || uploading || videoUploading) return;
    const trimmed = value.trim();

    if (pendingImageUrl) {
      onSendImage?.(pendingImageUrl, trimmed);
      clearImage();
      setValue('');
      stopTyping();
      resetHeight();
      onCancelReply?.();
      return;
    }

    if (pendingVideoUrl) {
      onSendVideo?.(pendingVideoUrl, trimmed);
      clearVideo();
      setValue('');
      stopTyping();
      resetHeight();
      onCancelReply?.();
      return;
    }

    if (!trimmed) return;
    onSend(trimmed, replyingTo?.id ?? null);
    setValue('');
    stopTyping();
    resetHeight();
    onCancelReply?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const handlePickImage = () => fileInputRef.current?.click();

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo ficheiro
    if (!file) return;

    setUploading(true);
    // Comprime/redimensiona ANTES do preview e do upload. Fotos da câmera
    // traseira (vários MB) faziam a webview recarregar por pressão de memória.
    const prepared = await compressImage(file);

    const localPreview = URL.createObjectURL(prepared);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return localPreview;
    });
    setPendingImageUrl(null);

    const res = await uploadImage(prepared);
    setUploading(false);

    if (res.success && res.data) {
      setPendingImageUrl(res.data.attachment_url);
    } else {
      // Falha no upload → limpar pré-visualização
      URL.revokeObjectURL(localPreview);
      setPreview(null);
    }
  };

  /* Vídeo como ANEXO: mostra a pré-visualização e faz upload, mas NÃO envia —
     fica "pendente" para a pessoa escrever uma legenda e mandar tudo junto
     (igual à imagem). Guarda de tamanho no cliente antes de subir. */
  const handleVideoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo ficheiro
    if (!file) return;
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      alert(
        `O vídeo é muito grande (máx. ${MAX_VIDEO_MB} MB). Grave um clipe mais ` +
          `curto ou reduza a qualidade da câmera.`
      );
      return;
    }
    const localPreview = URL.createObjectURL(file);
    setVideoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return localPreview;
    });
    setPendingVideoUrl(null);
    setVideoUploading(true);
    const res = await uploadVideo(file);
    setVideoUploading(false);
    if (res.success && res.data) {
      setPendingVideoUrl(res.data.attachment_url);
    } else {
      URL.revokeObjectURL(localPreview);
      setVideoPreview(null);
      alert(res.error || 'Falha ao enviar o vídeo.');
    }
  };

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    setShowEmoji(false);
    if (!el) {
      setValue((v) => v + emoji);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    setValue(next);
    signalTyping();
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  /* Carrega figurinhas ao abrir o seletor e a cada busca (com debounce).
     Sem termo → tendências; com termo → busca. */
  useEffect(() => {
    if (!showStickers) return;
    let cancelled = false;
    const q = stickerQuery.trim();
    setStickersLoading(true);
    const timer = setTimeout(
      async () => {
        const res = q ? await searchStickers(q) : await getTrendingStickers();
        if (cancelled) return;
        setStickerResults(res.success && res.data ? res.data.stickers : []);
        setStickersLoading(false);
      },
      q ? 350 : 0
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showStickers, stickerQuery]);

  const handlePickSticker = useCallback(
    (id: string) => {
      // Fecha o seletor IMEDIATAMENTE (resposta instantânea ao clique). O
      // download/rehost da figurinha e o envio via socket seguem em background,
      // sem travar a UI — a figurinha aparece na conversa assim que fica pronta.
      setShowStickers(false);
      setStickerQuery('');
      void pickSticker(id).then((res) => {
        if (res.success && res.data) onSendSticker?.(res.data.attachment_url);
      });
    },
    [onSendSticker]
  );

  const toggleEmoji = () => {
    setShowEmoji((v) => !v);
    setShowStickers(false);
    setShowAttach(false);
  };

  const toggleAttach = () => {
    setShowAttach((v) => !v);
    setShowEmoji(false);
    setShowStickers(false);
  };

  /* Sugestões de digitação (texto preditivo LOCAL, sem IA). Derivadas do texto
     atual — trabalho leve, sem debounce necessário. Só quando focado e com texto. */
  const suggestions = useMemo(
    () => (focused && value.trim() ? getSuggestions(value) : []),
    [focused, value]
  );

  /* Aplica a sugestão: completa o texto do campo e refoca (NÃO envia). */
  const applySuggestion = useCallback((suggestion: string) => {
    setValue(suggestion);
    signalTyping();
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const pos = suggestion.length;
      el.setSelectionRange(pos, pos);
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    });
  }, [signalTyping]);

  const canSend =
    !disabled &&
    !uploading &&
    !videoUploading &&
    (!!value.trim() || !!pendingImageUrl || !!pendingVideoUrl);

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Pré-visualização da imagem escolhida */}
      {preview && (
        <div>
          <div className={styles.imagePreview}>
            <img src={preview} alt="Pré-visualização do anexo" />
            <button
              type="button"
              className={styles.imagePreviewRemove}
              onClick={clearImage}
              aria-label="Remover imagem"
            >
              <X size={14} />
            </button>
          </div>
          {uploading && <p className={styles.uploadingHint}>A enviar imagem…</p>}
        </div>
      )}

      {/* Pré-visualização do vídeo escolhido (anexo com legenda opcional) */}
      {videoPreview && (
        <div>
          <div className={styles.imagePreview}>
            <video
              src={videoPreview}
              muted
              playsInline
              controls
              style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, display: 'block' }}
            />
            <button
              type="button"
              className={styles.imagePreviewRemove}
              onClick={clearVideo}
              aria-label="Remover vídeo"
            >
              <X size={14} />
            </button>
          </div>
          {videoUploading && <p className={styles.uploadingHint}>A enviar vídeo…</p>}
        </div>
      )}

      {recording || audioUploading ? (
        <div className={styles.recordBar}>
          {audioUploading ? (
            <span className={styles.recHint}>A enviar áudio…</span>
          ) : (
            <>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => finishRecording(true)}
                aria-label="Cancelar gravação"
              >
                <Trash2 size={20} />
              </button>
              <span className={styles.recDot} aria-hidden="true" />
              <span className={styles.recTime}>{formatSeconds(recSeconds)}</span>
              <span className={styles.recHint}>A gravar…</span>
              <button
                type="button"
                className={styles.sendBtn}
                onClick={() => finishRecording(false)}
                aria-label="Enviar áudio"
              >
                <Send size={20} />
              </button>
            </>
          )}
        </div>
      ) : (
      <>
      {/* Banner de resposta (citação) — acima do campo */}
      {replyingTo && (
        <div className={styles.replyBanner}>
          <Reply size={16} className={styles.replyBannerIcon} />
          <div className={styles.replyBannerBody}>
            <span className={styles.replyBannerAuthor}>{replyingTo.author}</span>
            <span className={styles.replyBannerText}>{replyingTo.text}</span>
          </div>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onCancelReply}
            aria-label="Cancelar resposta"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {/* Barra de sugestões (texto preditivo) — rolável, acima do campo */}
      {suggestions.length > 0 && (
        <div
          className={styles.suggestionBar}
          role="listbox"
          aria-label="Sugestões de texto"
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s}-${i}`}
              type="button"
              className={styles.suggestionChip}
              // Evita perder o foco do campo antes do clique registar (senão a barra some).
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applySuggestion(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <div className={styles.inputRow}>
        {/* Anexos recolhidos num "+" (estilo WhatsApp): foto, câmera, figurinha */}
        <div className={styles.attachWrapper}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={toggleAttach}
            disabled={disabled || uploading || videoUploading}
            aria-label="Anexar"
            aria-expanded={showAttach}
          >
            <Plus size={22} />
          </button>
          {showAttach && (
            <div className={styles.attachMenu} role="menu">
              <button
                type="button"
                className={styles.attachItem}
                onClick={() => {
                  setShowAttach(false);
                  handlePickImage();
                }}
              >
                <ImagePlus size={18} /> Foto / galeria
              </button>
              {isMobile && (
                <button
                  type="button"
                  className={styles.attachItem}
                  onClick={() => {
                    setShowAttach(false);
                    cameraInputRef.current?.click();
                  }}
                >
                  <Camera size={18} /> Câmera
                </button>
              )}
              {isMobile && (
                <button
                  type="button"
                  className={styles.attachItem}
                  onClick={() => {
                    setShowAttach(false);
                    videoCaptureInputRef.current?.click();
                  }}
                >
                  <Clapperboard size={18} /> Gravar vídeo
                </button>
              )}
              <button
                type="button"
                className={styles.attachItem}
                onClick={() => {
                  setShowAttach(false);
                  videoInputRef.current?.click();
                }}
              >
                <Video size={18} /> Vídeo{isMobile ? ' / galeria' : ''}
              </button>
              <button
                type="button"
                className={styles.attachItem}
                onClick={() => {
                  setShowAttach(false);
                  setShowEmoji(false);
                  setShowStickers(true);
                }}
              >
                <Sticker size={18} /> Figurinha
              </button>
            </div>
          )}
          {/* Popover de figurinhas — ancorado ao "+" */}
          {showStickers && (
            <div className={styles.stickerPopover} role="menu">
              <input
                className={styles.stickerSearch}
                type="text"
                value={stickerQuery}
                onChange={(e) => setStickerQuery(e.target.value)}
                placeholder="Buscar figurinhas…"
                aria-label="Buscar figurinhas"
                autoFocus
              />
              <div className={styles.stickerGrid}>
                {stickersLoading ? (
                  <p className={styles.stickerHint}>A carregar…</p>
                ) : stickerResults.length === 0 ? (
                  <p className={styles.stickerHint}>Nenhuma figurinha encontrada.</p>
                ) : (
                  stickerResults.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={styles.stickerItem}
                      onClick={() => handlePickSticker(s.id)}
                      aria-label={s.title || 'Figurinha'}
                    >
                      <img src={s.preview_url} alt={s.title || 'Figurinha'} loading="lazy" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Inputs escondidos (galeria + câmera) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
        {isMobile && (
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            aria-hidden="true"
          />
        )}
        {/* Vídeo (galeria/PC): input sem capture — no celular o SO ainda oferece
            "Gravar" OU "Biblioteca"; no PC, seletor de arquivo. */}
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          onChange={handleVideoChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
        {/* Gravar vídeo (celular): capture abre a câmera de vídeo DIRETO, igual ao
            botão de Câmera da foto. Mesmo handler (guarda de 50 MB + upload). */}
        {isMobile && (
          <input
            ref={videoCaptureInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            onChange={handleVideoChange}
            style={{ display: 'none' }}
            aria-hidden="true"
          />
        )}

        {/* Emoji visível */}
        <div className={styles.emojiWrapper}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={toggleEmoji}
            disabled={disabled}
            aria-label="Inserir emoji"
            aria-expanded={showEmoji}
          >
            <Smile size={20} />
          </button>
          {showEmoji && (
            <div className={styles.emojiPopover} role="menu">
              {EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={styles.emojiItem}
                  onClick={() => insertEmoji(emoji)}
                  aria-label={`Inserir ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (e.target.value.trim()) signalTyping();
            else stopTyping();
          }}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={
            pendingImageUrl || pendingVideoUrl ? 'Legenda (opcional)…' : placeholder
          }
          disabled={disabled}
          rows={1}
          aria-label="Campo de mensagem"
        />

        {/* Enviar (quando há texto/imagem) OU microfone — toggle estilo WhatsApp */}
        {canSend ? (
          <button type="submit" className={styles.sendBtn} aria-label="Enviar mensagem">
            <Send size={20} />
          </button>
        ) : (
          <button
            type="button"
            className={styles.sendBtn}
            onClick={startRecording}
            disabled={disabled || uploading}
            aria-label="Gravar áudio"
          >
            <Mic size={20} />
          </button>
        )}
      </div>
      </>
      )}
    </form>
  );
}
